import BaseLayout from '@lcsc-ui/layouts/baseLayout';
import ecpCurl from '@lcsc-ui/utils/ecpCurl';
import Head from 'next/head';

import OpApi from '@/apis/op';
import StaticdataApi from '@/apis/staticdata';
import WwwApi from '@/apis/www';
import XinpinProductPage from '@/features/xinpin/XinpinProductPage';

// 产品类型映射配置
const PRODUCT_CONFIG = {
  GD32E231: {
    productType: 'GD32E231',
    apiKey: 'xinpinGD32E231',
    title: '【新品推荐】超值MCU! GD32E231系列的工业化部署-立创商城',
    keywords: 'MUC,GD32E231,微控制器, GD32E231数据表',
    description:
      'GD32E231系列超值型MCU新品以全面优化的处理器资源和最为经济的成本优势，将创新的Arm® Cortex®-M23内核引入高速信号采集、混合信号处理、电机控制、传感器网络等工业应用场合。',
    canonical: 'https://www.szlcsc.com/xinpin/GD32E231.html',
  },
  AFM201T: {
    productType: 'AFM201T',
    apiKey: 'xinpinAFM201T',
    title: '九九物联AFM201T2.4G WiFi+蓝牙4.2共存单芯片全新模组-立创商城',
    keywords: 'AFM201T,wifi蓝牙模块,九九物联,智能家居,联网简单',
    description: 'AFM201T是基于Realtek RTL8720C开发的全新模组，定位IoT智能设备市场，是新一代WiFi+蓝牙共存方案。',
    canonical: 'https://www.szlcsc.com/xinpin/AFM201T.html',
  },
  'XILINX-PYNQ-Z2': {
    productType: 'XILINXPYNQZ2',
    apiKey: 'xinpinXILINXPYNQZ2',
    title: 'XILINX PYNQ-Z2开源框架的第二代最新开发平台-立创商城',
    keywords: '赛灵思, FPGA芯片, XC7Z020，可嵌入式编程',
    description: 'PYNQ-Z2开发板是Xilinx开源框架的第二代最新开发平台，支持PYNQ即Python on ZYNQ。',
    canonical: 'https://www.szlcsc.com/xinpin/XILINX-PYNQ-Z2.html',
  },
};

export default function XinpinProductDetailPage({
  config,
  resourceData,
  websiteSectionList,
  catalogList,
  headerAd,
  xinpinData,
}) {
  if (!config) {
    return null;
  }
  const { productRecordList = [], productRecordList2 = [] } = xinpinData || {};

  // 根据环境变量拼接 wwwTemplateResource
  const env = typeof window === 'object' ? process.env.NEXT_PUBLIC_APP_ENV : process.env.APP_ENV;
  let wwwTemplateResource = '';
  if (env === 'pro') {
    wwwTemplateResource = 'https://static.szlcsc.com/template'; // 生产环境
  } else if (env === 'fat') {
    wwwTemplateResource = 'https://fatwsimg.szlcsc.com/template'; // fat环境
  } else {
    wwwTemplateResource = 'https://devwsimg.szlcsc.com/template'; // dev环境
  }

  // 从 resourceData 获取 lcscItemUrl
  const lcscItemUrl = resourceData?.projectConfigVO?.lcscItemUrl || '';

  return (
    <>
      <Head>
        <title>{config.title}</title>
        <meta name="keywords" content={config.keywords} />
        <meta name="description" content={config.description} />
        <link rel="canonical" href={config.canonical} />
      </Head>
      <BaseLayout
        resourceData={resourceData}
        websiteSectionList={websiteSectionList}
        headerAd={headerAd}
        catalogList={catalogList}
        isLgScreen={false}
        notIndexHeaderAdapter
        onlySScreen={true}
        isNeedTopAd={true}
      >
        <XinpinProductPage
          productType={config.productType}
          productRecordList={productRecordList}
          productRecordList2={productRecordList2}
          wwwTemplateResource={wwwTemplateResource}
          lcscItemUrl={lcscItemUrl}
        />
      </BaseLayout>
    </>
  );
}

export async function getStaticPaths() {
  return {
    paths: [].map((id) => ({
      params: { id },
    })),
    fallback: 'blocking', // 或者使用 false 如果只支持预定义的路径
  };
}

export async function getStaticProps(context) {
  const { params } = context;
  const { id } = params;

  // 获取产品配置
  const configId = id.replace('.html', '');
  const config = PRODUCT_CONFIG[configId];

  if (!config) {
    return {
      notFound: true,
      revalidate: 1,
    };
  }

  try {
    // 获取基础数据
    const resourceListApi = StaticdataApi.resourceList;
    const catalogApi = StaticdataApi.catalogList;

    const [websiteSectionList, resourceData, catalogList, headerAd] = await Promise.all([
      ecpCurl(OpApi.websiteSection, { enableCache: true }),
      ecpCurl(resourceListApi, { enableCache: true }),
      ecpCurl(catalogApi, { enableCache: true }),
      ecpCurl(OpApi.advertisingHeader, { enableCache: true }),
    ]);

    // 获取新品详情数据
    let xinpinData = {
      productRecordList: [],
      productRecordList2: [],
    };

    try {
      const xinpinResponse = await ecpCurl(WwwApi[config.apiKey]);
      if (xinpinResponse) {
        // 确保数据格式正确，每个产品都有 productVO
        const formatProductList = (list) => {
          if (!Array.isArray(list)) return [];
          return list.map((item) => {
            // 如果已经有 productVO，直接返回
            if (item.productVO) return item;
            // 如果没有 productVO，将整个对象作为 productVO
            return {
              productVO: item,
              isGroupProduct: item.isGroupProduct || false,
              isSecondKill: item.isSecondKill || false,
              moreChannelVO: item.moreChannelVO || [],
            };
          });
        };

        xinpinData = {
          productRecordList: formatProductList(xinpinResponse.productRecordList || []),
          productRecordList2: formatProductList(xinpinResponse.productRecordList2 || []),
        };
      }
    } catch (error) {
      console.error(`Error fetching xinpin data for ${config.apiKey}:`, error);
      // 使用mock数据进行测试
      xinpinData = {
        productRecordList: [],
        productRecordList2: [],
      };
    }

    return {
      props: {
        config,
        resourceData,
        websiteSectionList,
        catalogList,
        headerAd,
        xinpinData,
      },
      revalidate: 60 * 60 * 6, // ISR: 每6小时重新生成一次
    };
  } catch (error) {
    console.error('Error fetching data:', error);
    return {
      props: {
        config,
        resourceData: {},
        websiteSectionList: {},
        catalogList: {},
        headerAd: {},
        xinpinData: {
          productRecordList: [],
          productRecordList2: [],
        },
      },
      revalidate: 1, // 出错时1分钟后重试
    };
  }
}
